// src/components/Cursos/CursosInscripcion.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Paper,
    Button,
    Snackbar,
    Alert,
    Tooltip,
    Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import { useAuth } from "../../context/AuthContext";

const API = process.env.REACT_APP_API_URL;

/* ====== Igual que EventosColegiado ====== */
const headersAuth = () => {
    const token = localStorage.getItem("token");
    return { Authorization: token ? `Bearer ${token}` : undefined };
};
/* ======================================= */

const EstadoPill = ({ estado }) => {
    const e = String(estado || "-").toUpperCase();

    const map = {
        PENALIZADA: { border: "error.main", text: "error.main" },
        RECHAZADA: { border: "error.main", text: "error.main" },
        PENDIENTE: { border: "warning.main", text: "warning.main" },
        ACEPTADA: { border: "success.main", text: "success.main" },
        APROBADA: { border: "success.main", text: "success.main" },
        FINALIZADA: { border: "info.main", text: "info.main" },
        REEMBOLSADA: { border: "secondary.main", text: "secondary.main" },
        DEVUELTA: { border: "secondary.main", text: "secondary.main" },
        CANCELADA: { border: "text.secondary", text: "text.secondary" },
        "-": { border: "divider", text: "text.secondary" },
    };

    const sty = map[e] || { border: "divider", text: "text.primary" };

    return (
        <Chip
            label={e}
            variant="outlined"
            size="small"
            sx={{
                height: 20,
                borderColor: sty.border,
                color: sty.text,
                fontWeight: 700,
                textTransform: "uppercase",
                "& .MuiChip-label": {
                    px: 0.75,
                    py: 0,
                    lineHeight: 1,
                    fontSize: 11,
                },
            }}
        />
    );
};

const CursosInscripcion = () => {
    const [cursos, setCursos] = useState([]);
    const [inscripciones, setInscripciones] = useState([]);
    const [mensaje, setMensaje] = useState(null);
    const [error, setError] = useState(null);

    // (no lo tocamos, pero lo dejamos porque ya lo tenías)
    const { token } = useAuth();

    /* ==========================
       BLOQUEO por estado > 6
       (COPIA razonamiento Eventos)
    ========================== */
    const [miEstadoId, setMiEstadoId] = useState(null);
    const [checkingCondicion, setCheckingCondicion] = useState(false);

    const api = useMemo(
        () => axios.create({ baseURL: API, withCredentials: false }),
        []
    );

    const extractEstadoId = (c) => {
        // calcado de eventos, pero más tolerante si viene objeto
        const v =
            c?.estadoId ??
            c?.estado_id ??
            c?.estado?.id ??
            (typeof c?.estado === "object" ? c?.estado?.id : c?.estado);

        const n = v != null && String(v).trim() !== "" ? Number(v) : null;
        return Number.isFinite(n) ? n : null;
    };

    const fetchMiPerfil = useCallback(async () => {
        const headers = headersAuth();
        const { data } = await api.get("/colegiado/perfil", { headers });
        return data || null;
    }, [api]);

    const recargarCondicion = useCallback(async () => {
        try {
            setCheckingCondicion(true);

            const perfil = await fetchMiPerfil();
            if (!perfil) {
                setMiEstadoId(null);
                return;
            }

            const estadoId = extractEstadoId(perfil);
            setMiEstadoId(estadoId);

            // Debug (déjalo 1 prueba y luego quítalo)
            console.log("[CursosInscripcion] estadoId =", estadoId, "raw =", perfil);
        } catch (e) {
            console.error("Error comprobando condición (perfil):", e);
            setMiEstadoId(null);
        } finally {
            setCheckingCondicion(false);
        }
    }, [fetchMiPerfil]);

    const bloqueadoPorCondicion = miEstadoId != null && Number(miEstadoId) > 6;
    /* ========================== */

    const formatearFecha = (fecha) => {
        if (!fecha) return "";
        return new Date(fecha).toLocaleDateString("es-ES");
    };

    const cargarInscripciones = () => {
        axios
            .get(`${process.env.REACT_APP_API_URL}/inscripciones/mias`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => setInscripciones(Array.isArray(res.data) ? res.data : []))
            .catch((err) => {
                console.error("Error al cargar inscripciones:", err);
                setInscripciones([]);
            });
    };

    const cargarCursosDisponibles = () => {
        axios
            .get(`${process.env.REACT_APP_API_URL}/api/cursos/disponibles`)
            .then((res) => {
                const data = (res.data || []).map((curso) => ({
                    id: curso.id,
                    nombre: curso.nombre,
                    fecha_desde: curso.fecha_desde,
                    fecha_hasta: curso.fecha_hasta,
                    numero_alumnos: curso.numero_alumnos,
                }));
                setCursos(data);
            })
            .catch((err) => {
                console.error("Error al cargar cursos disponibles:", err);
            });
    };

    // true si el usuario tiene alguna inscripción PENALIZADA
    const tienePenalizacion = useMemo(
        () =>
            inscripciones?.some(
                (i) => String(i?.estado || "").toUpperCase() === "PENALIZADA"
            ) || false,
        [inscripciones]
    );

    const inscribirse = (cursoId) => {
        // === COPIA de eventos: cortafuegos primero ===
        if (checkingCondicion) {
            setError("Comprobando tu estado... inténtalo de nuevo en unos segundos.");
            return;
        }
        if (bloqueadoPorCondicion) {
            setError("Debido a su condición actual no es posible inscribirse en ningún curso.");
            return;
        }
        // ============================================

        if (tienePenalizacion) {
            setError("No puedes inscribirte: tienes una inscripción con estado PENALIZADA.");
            return;
        }

        axios
            .post(
                `${process.env.REACT_APP_API_URL}/inscripciones/curso/${cursoId}`,
                null,
                { headers: { Authorization: `Bearer ${token}` } }
            )
            .then(() => {
                setMensaje("Inscripción registrada correctamente.");
                cargarInscripciones();
            })
            .catch((err) => {
                console.error("Error al inscribirse:", err);
                setError(
                    err?.response?.data ||
                    "No se pudo inscribir al curso. Inténtalo más tarde."
                );
            });
    };

    const obtenerEstadoInscripcion = (cursoId) => {
        const inscripcion = inscripciones.find((i) => i?.curso?.id === cursoId);
        return inscripcion ? inscripcion.estado : null;
    };

    const columnas = [
        { field: "nombre", headerName: "Nombre del curso", flex: 1 },
        {
            field: "fecha_desde",
            headerName: "Fecha desde",
            flex: 0.5,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (params.value ? formatearFecha(params.value) : ""),
        },
        {
            field: "fecha_hasta",
            headerName: "Fecha hasta",
            flex: 0.5,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (params.value ? formatearFecha(params.value) : ""),
        },
        {
            field: "estado",
            headerName: "Estado",
            flex: 0.5,
            headerAlign: "center",
            align: "center",
            sortable: false,
            renderCell: (params) => {
                const estado = obtenerEstadoInscripcion(params.row.id) || "-";
                return <EstadoPill estado={estado} />;
            },
        },
        {
            field: "acciones",
            headerName: "Acciones",
            flex: 0.5,
            headerAlign: "center",
            align: "center",
            sortable: false,
            renderCell: (params) => {
                const cursoId = params.row.id;
                const estado = obtenerEstadoInscripcion(cursoId);

                if (estado) {
                    return (
                        <Typography variant="body2" color="text.secondary">
                            —
                        </Typography>
                    );
                }

                // === COPIA de eventos: deshabilitar por condición ===
                const disabled = tienePenalizacion || bloqueadoPorCondicion || checkingCondicion;

                const tooltip = checkingCondicion
                    ? "Comprobando tu estado..."
                    : bloqueadoPorCondicion
                        ? "Debido a tu condición actual no puedes inscribirte."
                        : "No puedes inscribirte: tienes una inscripción PENALIZADA.";
                // ====================================================

                const btn = (
                    <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={() => inscribirse(cursoId)}
                        disabled={disabled}
                    >
                        Inscribirse
                    </Button>
                );

                return disabled ? (
                    <Tooltip title={tooltip}>
                        <span>{btn}</span>
                    </Tooltip>
                ) : (
                    btn
                );
            },
        },
    ];

    useEffect(() => {
        cargarCursosDisponibles();
        cargarInscripciones();
        recargarCondicion(); // 👈 igual que eventos: se comprueba al montar
    }, []);

    return (
        <Box sx={{ p: 2 }}>
            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h4" gutterBottom>
                    Cursos presenciales disponibles para inscripción
                </Typography>

                {/* === COPIA de eventos: info de comprobación y bloqueo === */}
                {checkingCondicion && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Comprobando tu estado...
                    </Alert>
                )}

                {bloqueadoPorCondicion && !checkingCondicion && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Debido a su condición actual no es posible inscribirse en ningún curso.
                    </Alert>
                )}
                {/* ======================================================== */}

                {/* Aviso si está penalizado (tal cual) */}
                {tienePenalizacion && (
                    <Alert severity="warning" variant="filled" sx={{ mb: 2, fontWeight: 600 }}>
                        Tienes una inscripción con estado <strong>PENALIZADA</strong>. Mientras dure,
                        no podrás inscribirte en nuevos cursos. Contacta con el Colegio para más información.
                    </Alert>
                )}

                <Typography variant="h6" gutterBottom>
                    Cursos online aquí
                </Typography>

                <Box sx={{ width: "100%" }}>
                    <DataGrid
                        rows={cursos}
                        columns={columnas}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 25, page: 0 } },
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        autoHeight
                        disableColumnMenu
                        disableSelectionOnClick
                        density="compact"
                        rowHeight={60}
                        columnHeaderHeight={56}
                        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                    />
                </Box>
            </Paper>

            <Snackbar open={!!mensaje} autoHideDuration={4000} onClose={() => setMensaje(null)}>
                <Alert severity="success" onClose={() => setMensaje(null)}>
                    {mensaje}
                </Alert>
            </Snackbar>

            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CursosInscripcion;
